import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'

import { API } from '@/api'
import LogoIcon from '@/assets/images/logo-icon.svg?react'
import { PageLoading } from '@/components/PageLoading'

const X_BINDING_EXISTS_CODE = 111
const REDIRECT_DELAY_SECONDS = 5
const REDIRECT_PATH = '/profile'

type CallbackStatus = 'loading' | 'success' | 'error'

function getCallbackParams() {
  return Object.fromEntries(new URLSearchParams(window.location.search).entries())
}

export function XFollowCallbackPage() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [status, setStatus] = useState<CallbackStatus>('loading')
  const [errorMessage, setErrorMessage] = useState('')
  const [secondsLeft, setSecondsLeft] = useState(REDIRECT_DELAY_SECONDS)

  useEffect(() => {
    const params = getCallbackParams()

    if (!params.state || !params.code) {
      setErrorMessage(
        t('profile.socialMedia.x.callbackFailed', 'Unable to confirm X follow. Please try again.'),
      )
      setStatus('error')
      return
    }

    API.callbackXFollow(params)
      .then((response) => {
        if (response.code === 0) {
          setStatus('success')
          return
        }

        if (response.code === X_BINDING_EXISTS_CODE) {
          setErrorMessage(
            t('common.errorCodes.111', {
              defaultValue: 'This X account is already linked to another Glows account.',
            }),
          )
        } else {
          setErrorMessage(
            t(
              'profile.socialMedia.x.callbackFailed',
              'Unable to confirm X follow. Please try again.',
            ),
          )
        }
        setStatus('error')
      })
      .catch(() => {
        setErrorMessage(
          t(
            'profile.socialMedia.x.callbackFailed',
            'Unable to confirm X follow. Please try again.',
          ),
        )
        setStatus('error')
      })
  }, [t])

  useEffect(() => {
    if (status !== 'success') return

    if (secondsLeft <= 0) {
      navigate(REDIRECT_PATH, { replace: true })
      return
    }

    const timer = setTimeout(() => setSecondsLeft((s) => s - 1), 1000)
    return () => clearTimeout(timer)
  }, [status, secondsLeft, navigate])

  if (status === 'loading') {
    return <PageLoading />
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-[image:var(--image-app-background)] bg-cover bg-center bg-no-repeat px-6">
      <div className="card bg-panel-white w-[400px] md:w-full flex flex-col items-center gap-4 text-center">
        <LogoIcon className="auth-logo-icon w-12 h-12" />
        <div className="flex flex-col items-center gap-2">
          <h1 className="t-bold text-primary">
            {status === 'success'
              ? t('profile.socialMedia.x.callbackSuccessTitle', 'X follow confirmed')
              : t('profile.socialMedia.x.callbackErrorTitle', 'Unable to confirm X follow')}
          </h1>
          <p className="t-sm text-secondary">
            {status === 'success'
              ? t('profile.socialMedia.x.callbackRedirectDesc', {
                  defaultValue: 'You will be redirected to the platform in {{seconds}} seconds',
                  seconds: secondsLeft,
                })
              : errorMessage ||
                t(
                  'profile.socialMedia.x.callbackErrorDesc',
                  'Please return to Glows.ai and try again.',
                )}
          </p>
        </div>
        <button
          type="button"
          className="t-sb text-blue cursor-pointer"
          onClick={() => navigate(REDIRECT_PATH, { replace: true })}
        >
          {status === 'success'
            ? t('profile.socialMedia.x.callbackRedirectNow', 'Redirect Now')
            : t('profile.socialMedia.x.callbackBackToPlatform', 'Back to Glows.ai')}
        </button>
      </div>
    </div>
  )
}
